import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, ThumbsUp, ThumbsDown, BookOpen, ChevronRight } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { logger } from '../utils/logger';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  'Getting Started', 'Using the Extension', 'PI Score and Analysis',
  'Rewrite and Tones', 'PI Craft', 'Content Generator',
  'Account and Privacy', 'Troubleshooting'
];

export default function HelpPage() {
  const [articles, setArticles] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const url = selectedCategory === 'all'
        ? `${API}/api/knowledge-base`
        : `${API}/api/knowledge-base?category=${encodeURIComponent(selectedCategory)}`;
      const res = await fetch(url);
      if (res.ok) setArticles((await res.json()).articles || []);
    } catch (err) { logger.error("Operation failed:", err); }
    finally { setLoading(false); }
  }, [selectedCategory]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  useEffect(() => {
    if (search.length >= 2) {
      const timer = setTimeout(() => searchArticles(search), 300);
      return () => clearTimeout(timer);
    } else if (search.length === 0) {
      fetchArticles();
    }
  }, [search, fetchArticles]);

  const searchArticles = async (q) => {
    try {
      const res = await fetch(`${API}/api/help/search?q=${encodeURIComponent(q)}`);
      const data = await res.json();
      setArticles(data.articles || []);
    } catch (err) { logger.error("Operation failed:", err); }
  };

  const fetchArticle = async (id) => {
    try {
      const res = await fetch(`${API}/api/help/articles/${id}`);
      const data = await res.json();
      setSelectedArticle(data);
    } catch (err) { logger.error("Operation failed:", err); }
  };

  const voteArticle = async (id, helpful) => {
    await fetch(`${API}/api/help/articles/${id}/helpful`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ helpful })
    });
  };

  if (selectedArticle) {
    return (
      <div className="min-h-screen bg-[#060C18] text-white">
        <Navbar />
        <main className="max-w-4xl mx-auto px-6 py-24">
          <button onClick={() => setSelectedArticle(null)} className="text-cyan-400 hover:underline mb-6 block">&larr; Back to Help Center</button>
          <span className="text-xs uppercase tracking-widest text-white/40 mb-2 block">{selectedArticle.category}</span>
          <h1 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{selectedArticle.title}</h1>
          <div className="prose prose-invert max-w-none text-white/70 leading-relaxed whitespace-pre-wrap">{selectedArticle.content}</div>
          <div className="mt-12 pt-6 border-t border-white/10">
            <p className="text-white/50 mb-3">Was this helpful?</p>
            <div className="flex gap-3">
              <button onClick={() => voteArticle(selectedArticle.id, true)} className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg hover:bg-white/10"><ThumbsUp className="w-4 h-4" /> Yes</button>
              <button onClick={() => voteArticle(selectedArticle.id, false)} className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg hover:bg-white/10"><ThumbsDown className="w-4 h-4" /> No</button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#060C18] text-white">
      <SEO title="Help Center" description="Get help with SendPI. Find answers about PI Check, PI Craft, Token Optimizer, Chrome Extension, MCP Server, and more." path="/help" />
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-24">
        <div className="text-center mb-12">
          <BookOpen className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
          <h1 data-testid="help-title" className="text-4xl font-bold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Help Center</h1>
          <p className="text-white/50">Find answers to your questions about SendPI</p>
        </div>

        <div className="relative max-w-xl mx-auto mb-12">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
          <input
            data-testid="help-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search articles..."
            className="w-full bg-white/5 border border-white/10 rounded-xl pl-12 pr-4 py-3 text-white focus:outline-none focus:border-cyan-500/50"
          />
        </div>

        <div className="flex flex-wrap gap-2 mb-8 justify-center">
          <button
            onClick={() => { setSelectedCategory('all'); setSearch(''); }}
            className={`px-4 py-2 rounded-full text-sm transition-colors ${selectedCategory === 'all' ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : 'bg-white/5 text-white/50 border border-white/10 hover:bg-white/10'}`}
          >
            All
          </button>
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => { setSelectedCategory(cat); setSearch(''); }}
              className={`px-4 py-2 rounded-full text-sm transition-colors ${selectedCategory === cat ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : 'bg-white/5 text-white/50 border border-white/10 hover:bg-white/10'}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {articles.length === 0 ? (
          <div className="text-center text-white/40 py-16">
            <p className="text-lg mb-2">No articles found</p>
            <p className="text-sm">Contact us at <a href="mailto:support@sendpi.ai" className="text-cyan-400">support@sendpi.ai</a></p>
          </div>
        ) : (
          <div className="grid gap-3">
            {articles.map(a => (
              <button
                key={a.id}
                onClick={() => fetchArticle(a.id)}
                className="bg-[#0E1830] border border-white/10 rounded-xl p-5 flex items-center justify-between hover:border-white/20 transition-colors text-left w-full"
              >
                <div>
                  <span className="text-xs uppercase tracking-widest text-white/30">{a.category}</span>
                  <h3 className="text-white font-medium mt-1">{a.title}</h3>
                </div>
                <ChevronRight className="w-5 h-5 text-white/30 shrink-0" />
              </button>
            ))}
          </div>
        )}

        <div className="mt-16 text-center text-white/40 text-sm">
          <p>Can't find what you need? Email <a href="mailto:support@sendpi.ai" className="text-cyan-400 hover:underline">support@sendpi.ai</a></p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
